import { useMemo } from 'react';
import type { EditorGraph } from '@/features/funnel-editor/domain/editor-graph';
import { PresentationCanvas } from '@/features/presentation/PresentationCanvas';
import { computeGraphEmphasis } from '@/domain/presentation/emphasis';
import { toPresentationGraphInput } from '@/features/funnel-editor/presentation/graph-bridge';
import { readPresentationExtension } from '@/features/funnel-editor/presentation/graph-bridge';
import type { ExportScope } from '@/domain/export-import/contracts';
import { PresentationBranding } from '@/features/presentation/PresentationBranding';
import { presentationLogoUrl } from '@/features/presentation/api/presentation-asset-api';

export function ExportSurface({
  graph,
  funnelId,
  funnelName,
  scope,
  transparent,
}: {
  graph: EditorGraph;
  funnelId: string;
  funnelName: string;
  scope: ExportScope;
  transparent: boolean;
}) {
  const input = useMemo(() => toPresentationGraphInput(graph), [graph]);
  const emphasis = useMemo(() => computeGraphEmphasis(input, {}), [input]);
  const presentation = readPresentationExtension(graph);
  const theme = scope === 'presentation' ? presentation.theme : 'dark';
  const logoUrl = presentation.companyLogoAssetId
    ? presentationLogoUrl(funnelId, presentation.companyLogoAssetId)
    : undefined;
  const graphPage = (
    <div
      data-export-page
      className={`flex h-[720px] w-[1280px] flex-col overflow-hidden ${
        transparent ? 'bg-transparent' : 'bg-bg-app'
      } text-text-primary`}
    >
      <header
        className={transparent ? 'px-6 py-4' : 'border-b border-border-subtle bg-surface px-6 py-4'}
      >
        {scope === 'presentation' ? (
          <PresentationBranding
            mode={presentation.brandingMode}
            companyName={presentation.companyName}
            logoUrl={logoUrl}
          />
        ) : (
          <p className="text-xs font-semibold uppercase tracking-widest text-text-brand">
            Dark Funnels
          </p>
        )}
        <h1 className="text-xl font-semibold">{funnelName}</h1>
        {scope === 'presentation' && presentation.title ? (
          <p className="text-sm text-text-secondary">{presentation.title}</p>
        ) : null}
      </header>
      <PresentationCanvas
        nodes={graph.nodes}
        edges={graph.edges}
        emphasis={emphasis}
        transparentBackground={transparent}
        {...(scope === 'current_view' ? { defaultViewport: graph.viewport } : {})}
      />
    </div>
  );
  return (
    <section data-testid="export-surface" data-theme={theme} className="w-[1280px]">
      {scope === 'presentation' && presentation.coverEnabled ? (
        <div
          data-export-page
          className="flex h-[720px] w-[1280px] flex-col items-center justify-center gap-8 bg-bg-app px-12 text-center text-text-primary"
        >
          <PresentationBranding
            mode={presentation.brandingMode}
            companyName={presentation.companyName}
            logoUrl={logoUrl}
          />
          <div>
            <h1 className="text-4xl font-semibold">{presentation.title || funnelName}</h1>
            {presentation.subtitle ? (
              <p className="mt-3 text-lg text-text-secondary">{presentation.subtitle}</p>
            ) : null}
          </div>
        </div>
      ) : null}
      {graphPage}
    </section>
  );
}
